import type {
  CustomerAttributeType,
  CustomerDocument,
  CustomerDocumentDetail,
  CustomerTemplate,
  CustomerTemplateSection,
  DocumentChunkStatus,
  DocumentSourceKind,
  ExtractionResult,
  TemplateAttribute,
} from '@protoss/shared';
import {
  ArchonIcon,
  Button,
  FileIcon,
  IconSize,
  LetterCaseUppercaseIcon,
  Pencil1Icon,
  ReloadIcon,
  RichTextEditor,
  Select,
  SelectItem,
  SkeletonBlock,
  TextArea,
  TextField,
  Tooltip,
  TrashIcon,
} from '@protoss/ui';
import * as React from 'react';
import { ConfirmDeleteDialog } from '../../../components/ConfirmDeleteDialog';
import { useDirtyStateContext } from '../../../contexts/DirtyStateContext';
import { useDirtyState } from '../../../hooks/useDirtyState';
import { useUnsavedChanges } from '../../../hooks/useUnsavedChanges';
import { formatRelativeTime } from '../../../lib/datetime';
import { formatBytes } from '../../../lib/format';
import { parseContentToTiptap, tiptapToPlainText } from '../../../lib/tiptap';
import type { DocumentAddDraft } from '../hooks/useCustomerDocuments';
import { useCustomerDocuments } from '../hooks/useCustomerDocuments';
import { customersApi } from '../services/customersApi';

// ─────────────────────────────────────────────────────────────────────────────
// Shared types
// ─────────────────────────────────────────────────────────────────────────────

export interface CustomerRailAttribute {
  fieldKey: string;
  name: string;
  fieldType: CustomerAttributeType;
  sectionKey: string;
  aiEnabled: boolean;
  description: string | null;
  aiPrompt?: string | null;
  scope?: 'workspace' | 'user' | 'customer';
  fromTemplate?: boolean;
}

export interface CustomerRailState {
  template: CustomerTemplate | null;
  attributes: CustomerRailAttribute[];
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

function formatTypeLabel(type: CustomerAttributeType): string {
  switch (type) {
    case 'text':
      return 'Short text';
    case 'long_text':
      return 'Long text';
    case 'multi_select':
      return 'Multi-select';
    case 'tags':
      return 'Tags';
    case 'select':
      return 'Select';
    case 'number':
      return 'Number';
    case 'boolean':
      return 'Boolean';
    default:
      return type;
  }
}

function resolveGroupLabel(sectionKey: string, sections?: CustomerTemplateSection[]): string {
  if (sections) {
    const match = sections.find((s) => s.sectionKey === sectionKey);
    if (match) return match.label;
  }
  return sectionKey;
}

// ─────────────────────────────────────────────────────────────────────────────
// AttributeDescriptionsPanel
// ─────────────────────────────────────────────────────────────────────────────

/**
 * AttributeDescriptionsPanel - read-only list of workspace + user attributes.
 *
 * Default panel shown in the right rail when creating or editing a customer.
 * Workspace-admin users get Edit buttons on user-scoped attributes.
 */
export function AttributeDescriptionsPanel({
  state,
  onEditAttribute,
  sectionFilter,
}: {
  state: CustomerRailState;
  onEditAttribute?: (attribute: CustomerRailAttribute) => void;
  /** When set, only attributes with this sectionKey are shown in the workspace section. */
  sectionFilter?: string;
}) {
  if (!state.template) {
    return (
      <div className="customer-rail" aria-busy="true" aria-label="Loading attribute descriptions">
        <div className="attr-desc-skeleton__section-label">
          <SkeletonBlock />
        </div>
        <div className="attr-desc-skeleton__list">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="attr-desc-skeleton__item">
              <div className="attr-desc-skeleton__item-top">
                <div className="attr-desc-skeleton__item-name">
                  <SkeletonBlock />
                </div>
                <div className="attr-desc-skeleton__item-type">
                  <SkeletonBlock />
                </div>
              </div>
              <div className="attr-desc-skeleton__item-desc">
                <SkeletonBlock />
              </div>
              <div className="attr-desc-skeleton__item-desc attr-desc-skeleton__item-desc--short">
                <SkeletonBlock />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const defaultAttrs = sectionFilter
    ? state.template.workspaceAttributes.filter((a) => a.sectionKey === sectionFilter)
    : state.template.workspaceAttributes;

  const customByFieldKey = new Map<string, CustomerRailAttribute>();
  for (const attribute of state.template.userAttributes) {
    customByFieldKey.set(attribute.fieldKey, {
      fieldKey: attribute.fieldKey,
      name: attribute.name,
      fieldType: attribute.fieldType,
      sectionKey: attribute.sectionKey,
      aiEnabled: attribute.aiEnabled,
      description: attribute.description,
      aiPrompt: attribute.aiPrompt ?? null,
      scope: 'user',
      fromTemplate: true,
    });
  }
  for (const attribute of state.attributes.filter((item) => item.scope === 'user')) {
    customByFieldKey.set(attribute.fieldKey, attribute);
  }

  const customAttrs = [...customByFieldKey.values()].sort(
    (a, b) => a.name.localeCompare(b.name) || a.fieldKey.localeCompare(b.fieldKey)
  );

  return (
    <div className="customer-rail">
      {defaultAttrs.length > 0 && (
        <section className="attr-desc-section">
          <h4 className="attr-desc-section__label">Workspace attributes</h4>
          <ul className="attr-desc-list" role="list">
            {defaultAttrs.map((attribute) => (
              <li key={attribute.fieldKey || attribute.name} className="attr-desc-item">
                <div className="attr-desc-item__top">
                  <div className="attr-desc-item__meta">
                    <span className="attr-desc-item__name">{attribute.name}</span>
                    <span className="attr-desc-item__type">
                      {formatTypeLabel(attribute.fieldType)}
                    </span>
                  </div>
                </div>
                <p className="attr-desc-item__desc">
                  {attribute.description?.trim() || 'No description yet.'}
                </p>
              </li>
            ))}
          </ul>
        </section>
      )}

      {customAttrs.length > 0 && (
        <section className="attr-desc-section">
          <h4 className="attr-desc-section__label">User attributes</h4>
          <ul className="attr-desc-list" role="list">
            {customAttrs.map((attribute) => (
              <li key={attribute.fieldKey || attribute.name} className="attr-desc-item">
                <div className="attr-desc-item__top">
                  <div className="attr-desc-item__meta">
                    <span className="attr-desc-item__name">{attribute.name}</span>
                    <span className="attr-desc-item__type">
                      {formatTypeLabel(attribute.fieldType)}
                    </span>
                  </div>
                  {onEditAttribute && attribute.fieldType !== 'tags' && (
                    <button
                      type="button"
                      className="attr-desc-item__edit-btn"
                      aria-label={`Edit ${attribute.name}`}
                      onClick={() => onEditAttribute(attribute)}
                    >
                      <Pencil1Icon width={IconSize.md} height={IconSize.md} aria-hidden="true" />
                    </button>
                  )}
                </div>
                <p className="attr-desc-item__desc">
                  {attribute.description?.trim() || 'No description yet.'}
                </p>
              </li>
            ))}
          </ul>
        </section>
      )}

      {defaultAttrs.length === 0 && customAttrs.length === 0 && (
        <p className="customer-rail__empty">No attribute definitions yet.</p>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// AttributeDesignerPanel
// ─────────────────────────────────────────────────────────────────────────────

/**
 * NewAttributeDraft - schema-only payload produced by AttributeDesignerPanel.
 *
 * Value entry happens inline in the customer form after the attribute is added.
 */
export interface NewAttributeDraft {
  name: string;
  fieldType: CustomerAttributeType;
  sectionKey: string;
  aiEnabled: boolean;
  description: string;
  aiPrompt: string;
}

const DESIGNER_ATTRIBUTE_TYPES: Array<{ value: CustomerAttributeType; label: string }> = [
  { value: 'text', label: 'Short text' },
  { value: 'boolean', label: 'Boolean' },
];

interface AttributeDesignerInitial {
  name: string;
  fieldType: CustomerAttributeType;
  sectionKey?: string;
  aiEnabled: boolean;
  description: string;
  aiPrompt?: string;
}

interface AttributeDesignerPanelProps {
  onSave: (draft: NewAttributeDraft) => void;
  onCancel: () => void;
  /** Emits whether current inputs are valid for submit (primarily for create mode). */
  onValidityChange?: (valid: boolean) => void;
  /** HTML id for the inner <form> element - lets external submit buttons use the form attribute. */
  formId?: string;
  /** When true the bottom action buttons are not rendered. */
  hideActions?: boolean;
  /** When true renders a skeleton while the template/data is still loading. */
  loading?: boolean;
  /**
   * Pre-fill values for edit mode. When provided, name and type render as
   * read-only displays; description and aiEnabled are editable.
   */
  initial?: AttributeDesignerInitial;
  /** Controls submit label and read-only field rendering. Default: 'create'. */
  mode?: 'create' | 'edit';
  /** Section key for create mode - determines which section the new attribute belongs to. */
  sectionKey?: string;
  /** Existing attribute names (case-insensitive) - used to catch duplicates while typing. */
  existingNames?: string[];
  /** Called when the user confirms deletion of a user attribute in edit mode. */
  onDelete?: () => void;
  /** Template sections for resolving section labels from the database. */
  sections?: CustomerTemplateSection[];
}

interface AttributeDesignerDirtyPayload {
  name: string;
  fieldType: CustomerAttributeType;
  sectionKey: string;
  aiEnabled: boolean;
  description: string;
  aiPrompt: string;
}

function buildAttributeDesignerDirtyPayload(args: {
  name: string;
  fieldType: CustomerAttributeType;
  sectionKey: string;
  aiEnabled: boolean;
  description: string;
  aiPrompt: string;
}): AttributeDesignerDirtyPayload {
  return {
    name: args.name.trim(),
    fieldType: args.fieldType,
    sectionKey: args.sectionKey,
    aiEnabled: args.aiEnabled,
    description: args.description.trim(),
    aiPrompt: args.aiPrompt.trim(),
  };
}

/**
 * AttributeDesignerPanel - right-rail form for creating or editing a customer attribute schema.
 *
 * In create mode: all fields editable, name field auto-focused.
 * In edit mode: name/type/group are read-only; description and aiEnabled are editable.
 */
export function AttributeDesignerPanel({
  onSave,
  onCancel,
  onValidityChange,
  formId,
  hideActions,
  loading,
  initial,
  mode = 'create',
  sectionKey: propSectionKey = 'identity',
  existingNames,
  onDelete,
  sections,
}: AttributeDesignerPanelProps) {
  const [name, setName] = React.useState(initial?.name ?? '');
  const [type, setType] = React.useState<CustomerAttributeType>(initial?.fieldType ?? 'text');
  const [aiEnabled, setAiEnabled] = React.useState(initial?.aiEnabled ?? true);
  const [description, setDescription] = React.useState(initial?.description ?? '');
  const [aiGuidance, setAiGuidance] = React.useState(initial?.aiPrompt ?? '');
  const [nameError, setNameError] = React.useState<string | null>(null);
  const [aiGuidanceError, setAiGuidanceError] = React.useState<string | null>(null);
  const nameInputRef = React.useRef<HTMLInputElement>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);

  // Sync form state when switching to a different attribute in edit mode
  React.useEffect(() => {
    if (initial) {
      setName(initial.name);
      setType(initial.fieldType ?? 'text');
      setAiEnabled(initial.aiEnabled);
      setDescription(initial.description ?? '');
      setAiGuidance(initial.aiPrompt ?? '');
      setNameError(null);
      setAiGuidanceError(null);
    }
  }, [initial]);

  // Auto-focus the name field when the panel mounts in create mode
  React.useEffect(() => {
    if (mode === 'create') {
      nameInputRef.current?.focus();
    }
  }, [mode]);

  const isEditMode = mode === 'edit';
  const {
    isDirty: dirtyStateIsDirty,
    setBaseline: setDirtyBaseline,
    updateCurrent: updateDirtyCurrent,
  } = useDirtyState<AttributeDesignerDirtyPayload>();

  const baselinePayload = React.useMemo(
    () =>
      buildAttributeDesignerDirtyPayload({
        name: initial?.name ?? '',
        fieldType: initial?.fieldType ?? 'text',
        sectionKey: initial?.sectionKey ?? propSectionKey,
        aiEnabled: initial?.aiEnabled ?? true,
        description: initial?.description ?? '',
        aiPrompt: initial?.aiPrompt ?? '',
      }),
    [initial, propSectionKey]
  );

  const currentPayload = React.useMemo(
    () =>
      buildAttributeDesignerDirtyPayload({
        name,
        fieldType: isEditMode ? (initial?.fieldType ?? type) : type,
        sectionKey: initial?.sectionKey ?? propSectionKey,
        aiEnabled,
        description,
        aiPrompt: aiGuidance,
      }),
    [
      aiEnabled,
      aiGuidance,
      description,
      initial?.fieldType,
      initial?.sectionKey,
      isEditMode,
      name,
      propSectionKey,
      type,
    ]
  );

  React.useEffect(() => {
    setDirtyBaseline(baselinePayload);
  }, [baselinePayload, setDirtyBaseline]);

  React.useEffect(() => {
    updateDirtyCurrent(currentPayload);
  }, [currentPayload, updateDirtyCurrent]);

  const checkNameDuplicate = React.useCallback(
    (value: string) => {
      if (!existingNames) return false;
      const normalized = value.trim().toLowerCase();
      return existingNames.some((n) => n.toLowerCase() === normalized);
    },
    [existingNames]
  );

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setNameError('Attribute name is required.');
      return;
    }
    if (checkNameDuplicate(name)) {
      setNameError('An attribute with this name already exists.');
      return;
    }
    if (aiEnabled && !aiGuidance.trim()) {
      setAiGuidanceError('AI extraction guidance is required when AI population is enabled.');
      return;
    }
    setNameError(null);
    setAiGuidanceError(null);
    onSave({
      name: name.trim(),
      fieldType: isEditMode ? (initial?.fieldType ?? type) : type,
      sectionKey: initial?.sectionKey ?? propSectionKey,
      aiEnabled,
      description: description.trim(),
      aiPrompt: aiGuidance.trim(),
    });
  };

  const isDirty = !loading && dirtyStateIsDirty;

  useUnsavedChanges(
    isDirty,
    'rr:attribute-designer',
    isEditMode ? `Edit attribute` : 'Create attribute'
  );

  const { confirmIfDirty } = useDirtyStateContext();
  const handleCancel = React.useCallback(() => {
    void confirmIfDirty('rr:attribute-designer').then((ok) => {
      if (!ok) return;
      onCancel();
    });
  }, [confirmIfDirty, onCancel]);

  React.useEffect(() => {
    if (!onValidityChange) return;
    if (isEditMode) {
      onValidityChange(true);
      return;
    }
    const isNamePresent = name.trim().length > 0;
    const hasDuplicateName = isNamePresent && checkNameDuplicate(name);
    const hasAiGuidance = !aiEnabled || aiGuidance.trim().length > 0;
    onValidityChange(isNamePresent && !hasDuplicateName && hasAiGuidance);
  }, [onValidityChange, isEditMode, name, aiEnabled, aiGuidance, checkNameDuplicate]);

  if (loading) {
    return (
      <div
        className="customer-rail customer-rail-skeleton"
        aria-busy="true"
        aria-label="Loading attribute form"
      >
        <div className="customer-rail-skeleton__intro">
          <div className="customer-rail-skeleton__intro-line">
            <SkeletonBlock />
          </div>
          <div className="customer-rail-skeleton__intro-line customer-rail-skeleton__intro-line--short">
            <SkeletonBlock />
          </div>
        </div>
        <div className="customer-rail-skeleton__field">
          <div className="customer-rail-skeleton__label">
            <SkeletonBlock />
          </div>
          <div className="customer-rail-skeleton__input">
            <SkeletonBlock />
          </div>
        </div>
        <div className="customer-rail-skeleton__field">
          <div className="customer-rail-skeleton__label">
            <SkeletonBlock />
          </div>
          <div className="customer-rail-skeleton__input">
            <SkeletonBlock />
          </div>
        </div>
        <div className="customer-rail-skeleton__field">
          <div className="customer-rail-skeleton__label">
            <SkeletonBlock />
          </div>
          <div className="customer-rail-skeleton__input">
            <SkeletonBlock />
          </div>
        </div>
        <div className="customer-rail-skeleton__checkbox">
          <SkeletonBlock />
        </div>
        <div className="customer-rail-skeleton__field">
          <div className="customer-rail-skeleton__label">
            <SkeletonBlock />
          </div>
          <div className="customer-rail-skeleton__textarea">
            <SkeletonBlock />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="customer-rail">
      <p className="customer-rail__intro">
        {isEditMode
          ? 'Edit the description and AI settings for this attribute.'
          : 'Define the attribute schema. After adding, the new field appears in the form immediately and is saved to your template on customer submit.'}
      </p>

      <form id={formId} className="customer-rail__designer-form" onSubmit={handleFormSubmit}>
        <div className="customer-form__field">
          <TextField
            ref={nameInputRef}
            label="Attribute name *"
            value={name}
            onChange={(e) => {
              const val = e.target.value;
              setName(val);
              if (val.trim() && checkNameDuplicate(val)) {
                setNameError('An attribute with this name already exists.');
              } else if (nameError) {
                setNameError(null);
              }
            }}
            placeholder="Seniority Level"
            error={nameError ?? undefined}
          />
        </div>

        <div className="customer-form__field">
          <label className="customer-form__field-label">Type</label>
          {isEditMode ? (
            <div className="customer-rail__designer-static-value">
              {initial ? formatTypeLabel(initial.fieldType ?? 'text') : ''}
            </div>
          ) : (
            <Select value={type} onValueChange={(v) => setType(v as CustomerAttributeType)}>
              {DESIGNER_ATTRIBUTE_TYPES.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </Select>
          )}
        </div>

        <div className="customer-form__field">
          <label className="customer-form__field-label">Group</label>
          <div className="customer-rail__designer-static-value" aria-readonly="true">
            {resolveGroupLabel(initial?.sectionKey ?? propSectionKey, sections)}
          </div>
        </div>

        <TextArea
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Explain why this attribute matters."
          rows={3}
        />

        <label className="customer-form__checkbox-row">
          <input
            type="checkbox"
            checked={aiEnabled}
            onChange={(e) => {
              const checked = e.target.checked;
              setAiEnabled(checked);
              if (!checked && aiGuidanceError) {
                setAiGuidanceError(null);
              }
            }}
          />
          <span>Allow AI to populate from meeting transcripts</span>
        </label>

        {aiEnabled && (
          <TextArea
            label="AI extraction guidance *"
            value={aiGuidance}
            onChange={(e) => {
              setAiGuidance(e.target.value);
              if (aiGuidanceError) setAiGuidanceError(null);
            }}
            placeholder="Explain how AI should interpret and extract this attribute."
            rows={3}
            error={aiGuidanceError ?? undefined}
          />
        )}
      </form>

      {!hideActions && (
        <div className="customer-rail__actions">
          <Button type="button" variant="ghost" size="sm" onClick={handleCancel}>
            Cancel
          </Button>
          <Button type="submit" form={formId} variant="primary" size="sm">
            {isEditMode ? 'Save changes' : 'Add attribute'}
          </Button>
        </div>
      )}

      {isEditMode && onDelete && (
        <>
          <div className="customer-rail__delete-section">
            <Button
              type="button"
              variant="destructive"
              size="sm"
              onClick={() => setDeleteDialogOpen(true)}
            >
              Delete attribute
            </Button>
          </div>
          <ConfirmDeleteDialog
            open={deleteDialogOpen}
            onOpenChange={setDeleteDialogOpen}
            title="Delete attribute"
            confirmKeyword={initial?.name ?? ''}
            description="This action cannot be undone. The attribute and all its values across customers will be permanently removed."
            onConfirm={() => {
              setDeleteDialogOpen(false);
              onDelete();
            }}
          />
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CustomerDocumentsPanel
// ─────────────────────────────────────────────────────────────────────────────

interface CustomerDocumentsPanelProps {
  customerUuid: string;
  onViewDocument: (
    docUuid: string,
    sourceKind: DocumentSourceKind,
    chunkStatus: DocumentChunkStatus | null
  ) => void;
  /** Optimistically-inserted doc (chunkStatus:'pending') from the add flow. */
  optimisticDocument?: CustomerDocument | null;
  /** Called once the refetch has absorbed the optimistic doc so it can be cleared. */
  onOptimisticDocumentConsumed?: () => void;
}

function isDocumentProcessing(chunkStatus: DocumentChunkStatus | null | undefined): boolean {
  return chunkStatus === 'pending';
}

function DocumentProcessingStatus() {
  return (
    <span className="customer-docs-panel__processing-status">
      <ReloadIcon
        width={12}
        height={12}
        className="customer-docs-panel__processing-spinner"
        aria-hidden="true"
      />
      <span>processing...</span>
    </span>
  );
}

export function CustomerDocumentsPanel({
  customerUuid,
  onViewDocument,
  optimisticDocument,
  onOptimisticDocumentConsumed,
}: CustomerDocumentsPanelProps) {
  const {
    documents: fetchedDocuments,
    isLoading,
    deleteDocument,
  } = useCustomerDocuments(customerUuid);

  // Once the server list contains the optimistic doc, signal the parent to clear it.
  React.useEffect(() => {
    if (!optimisticDocument) return;
    if (fetchedDocuments.some((d) => d.uuid === optimisticDocument.uuid)) {
      onOptimisticDocumentConsumed?.();
    }
  }, [fetchedDocuments, optimisticDocument, onOptimisticDocumentConsumed]);

  // Merge: show optimistic doc at the top if not yet in fetched list.
  const documents = React.useMemo(() => {
    if (!optimisticDocument) return fetchedDocuments;
    if (fetchedDocuments.some((d) => d.uuid === optimisticDocument.uuid)) return fetchedDocuments;
    return [optimisticDocument, ...fetchedDocuments];
  }, [fetchedDocuments, optimisticDocument]);
  const [deleteTarget, setDeleteTarget] = React.useState<{ uuid: string; title: string } | null>(
    null
  );

  if (isLoading && documents.length === 0) {
    return (
      <div className="attr-descriptions-panel">
        <SkeletonBlock className="attr-descriptions-panel__skeleton" />
        <SkeletonBlock className="attr-descriptions-panel__skeleton" />
        <SkeletonBlock className="attr-descriptions-panel__skeleton" />
      </div>
    );
  }

  if (documents.length === 0) {
    return (
      <div className="attr-descriptions-panel">
        <p className="attr-descriptions-panel__empty">
          No documents yet. Add one to start extracting profile data.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="attr-descriptions-panel customer-docs-panel">
        <ul className="customer-docs-panel__list" role="list">
          {documents.map((doc) => (
            <li key={doc.uuid} className="customer-docs-panel__row">
              <button
                type="button"
                className="customer-docs-panel__row-body"
                onClick={() => onViewDocument(doc.uuid, doc.sourceKind, doc.chunkStatus)}
              >
                <span className="customer-docs-panel__title">
                  {doc.title}
                  <span className="customer-docs-panel__kind-icon" aria-hidden="true">
                    {doc.sourceKind === 'upload' ? (
                      <FileIcon width={12} height={12} />
                    ) : (
                      <LetterCaseUppercaseIcon width={12} height={12} />
                    )}
                  </span>
                </span>
                <span className="customer-docs-panel__meta">
                  {formatRelativeTime(doc.updatedAt)}
                  {isDocumentProcessing(doc.chunkStatus) && <DocumentProcessingStatus />}
                </span>
              </button>
              <span className="customer-docs-panel__actions">
                <Tooltip.Root>
                  <Tooltip.Trigger asChild>
                    <button
                      type="button"
                      className="customer-docs-panel__delete-btn"
                      disabled={isDocumentProcessing(doc.chunkStatus)}
                      onClick={() =>
                        isDocumentProcessing(doc.chunkStatus)
                          ? undefined
                          : setDeleteTarget({ uuid: doc.uuid, title: doc.title })
                      }
                      aria-label={`Delete ${doc.title}`}
                    >
                      <TrashIcon width={IconSize.md} height={IconSize.md} aria-hidden="true" />
                    </button>
                  </Tooltip.Trigger>
                  {isDocumentProcessing(doc.chunkStatus) && (
                    <Tooltip.Portal>
                      <Tooltip.Content className="ui-tooltip" side="top" align="end" sideOffset={4}>
                        Document is being processed
                        <Tooltip.Arrow className="ui-tooltip-arrow" />
                      </Tooltip.Content>
                    </Tooltip.Portal>
                  )}
                </Tooltip.Root>
              </span>
            </li>
          ))}
        </ul>
      </div>

      <ConfirmDeleteDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
        title="Delete document"
        confirmKeyword={deleteTarget?.title ?? ''}
        description="This action cannot be undone. The document will be permanently removed."
        onConfirm={() => {
          if (!deleteTarget) return;
          void deleteDocument(deleteTarget.uuid);
          setDeleteTarget(null);
        }}
      />
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CustomerDocumentAddPanel
// ─────────────────────────────────────────────────────────────────────────────

interface CustomerDocumentAddPanelProps {
  /** Determines which form is rendered - decided by which header button the user clicked. */
  mode: 'paste' | 'upload';
  onSave: (draft: DocumentAddDraft) => Promise<void>;
  /** Called when save progresses through idle → saving → saved states. */
  onSaveStateChange?: (state: 'idle' | 'saving' | 'saved') => void;
  onCancel: () => void;
  /** Emits true when the form is submittable. */
  onValidityChange?: (valid: boolean) => void;
  /** HTML id for the inner <form>. */
  formId?: string;
  /** When true, bottom action buttons are hidden. */
  hideActions?: boolean;
  /**
   * When provided (alongside customerUuid), the panel is in edit mode:
   * fetches the existing document to pre-populate the form and calls PATCH on save.
   */
  docUuid?: string;
  customerUuid?: string;
  /** Called with the saved document after a successful edit, before onCancel fires. */
  onEditSaved?: (doc: CustomerDocumentDetail) => void;
}

export function CustomerDocumentAddPanel({
  mode,
  onSave,
  onCancel,
  onValidityChange,
  onSaveStateChange,
  formId,
  hideActions,
  docUuid,
  customerUuid,
  onEditSaved,
}: CustomerDocumentAddPanelProps) {
  const isEditMode = !!(docUuid && customerUuid);
  const saveResetTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const saveRequestIdRef = React.useRef(0);

  const [title, setTitle] = React.useState('');
  const [pasteBody, setPasteBody] = React.useState<Record<string, unknown> | null>(null);
  const [pasteText, setPasteText] = React.useState('');
  const [selectedFile, setSelectedFile] = React.useState<File | null>(null);
  const [isDragging, setIsDragging] = React.useState(false);
  const [saveState, setSaveState] = React.useState<'idle' | 'saving' | 'saved'>('idle');
  const [isLoadingDoc, setIsLoadingDoc] = React.useState(isEditMode);
  // Snapshot of server content - used to compute dirty in edit mode
  const [initialTitle, setInitialTitle] = React.useState('');
  const [initialText, setInitialText] = React.useState('');

  // In edit mode, pre-populate with existing document content
  React.useEffect(() => {
    if (!isEditMode) return;
    setIsLoadingDoc(true);
    customersApi
      .getDocument(customerUuid!, docUuid!)
      .then(({ document: d }) => {
        setTitle(d.title);
        const body = parseContentToTiptap(d.contentText);
        setPasteBody(body);
        setPasteText(tiptapToPlainText(body));
        setInitialTitle(d.title);
        setInitialText(d.contentText);
      })
      .catch((err) => console.error('[CustomerRightRailPanels] failed to load document', err))
      .finally(() => setIsLoadingDoc(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docUuid, customerUuid]);

  const pasteJsonStr = pasteBody ? JSON.stringify(pasteBody) : '';
  const dirty = isEditMode
    ? title !== initialTitle || pasteJsonStr !== initialText
    : mode === 'paste'
      ? title.trim().length > 0 || pasteText.trim().length > 0
      : title.trim().length > 0 || selectedFile !== null;
  useUnsavedChanges(
    saveState !== 'saved' && dirty,
    'rr:document-add',
    isEditMode ? 'Edit document' : 'Add document'
  );

  const { confirmIfDirty } = useDirtyStateContext();
  const handleCancel = React.useCallback(() => {
    void confirmIfDirty('rr:document-add').then((ok) => {
      if (!ok) return;
      onCancel();
    });
  }, [confirmIfDirty, onCancel]);

  const isValid = mode === 'paste' ? pasteText.trim().length > 0 : selectedFile !== null;

  React.useEffect(() => {
    onValidityChange?.(isEditMode ? isValid && dirty : isValid);
  }, [isValid, dirty, isEditMode, onValidityChange]);

  React.useEffect(() => {
    return () => {
      if (saveResetTimeoutRef.current) clearTimeout(saveResetTimeoutRef.current);
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid || saveState !== 'idle') return;
    saveRequestIdRef.current += 1;
    const requestId = saveRequestIdRef.current;
    if (saveResetTimeoutRef.current) {
      clearTimeout(saveResetTimeoutRef.current);
      saveResetTimeoutRef.current = null;
    }
    setSaveState('saving');
    onSaveStateChange?.('saving');
    try {
      const richText = pasteBody ? JSON.stringify(pasteBody) : pasteText;
      if (isEditMode) {
        const { document: saved } = await customersApi.updatePasteDocument(
          customerUuid!,
          docUuid!,
          richText,
          title || undefined
        );
        onEditSaved?.(saved);
      } else {
        const draft: DocumentAddDraft =
          mode === 'paste'
            ? { kind: 'paste', title, text: richText }
            : { kind: 'upload', title, file: selectedFile! };
        await onSave(draft);
      }
      if (requestId !== saveRequestIdRef.current) return;
      setSaveState('saved');
      onSaveStateChange?.('saved');
      if (isEditMode) {
        // Snapshot current values so dirty becomes false → Save button self-disables.
        // No navigation - avoids re-mounting the view panel and causing a visible refresh.
        setInitialTitle(title);
        setInitialText(pasteBody ? JSON.stringify(pasteBody) : pasteText);
        saveResetTimeoutRef.current = setTimeout(() => {
          if (requestId !== saveRequestIdRef.current) return;
          setSaveState('idle');
          onSaveStateChange?.('idle');
          saveResetTimeoutRef.current = null;
        }, 700);
      } else {
        saveResetTimeoutRef.current = setTimeout(() => {
          if (requestId !== saveRequestIdRef.current) return;
          saveResetTimeoutRef.current = null;
          onCancel();
        }, 700);
      }
    } catch {
      if (requestId !== saveRequestIdRef.current) return;
      setSaveState('idle');
      onSaveStateChange?.('idle');
    }
  };

  if (isLoadingDoc) {
    return (
      <div className="attr-descriptions-panel customer-doc-add-panel customer-doc-add-panel--paste">
        <div className="customer-doc-add-panel__form">
          {/* mirrors TextField: label + input */}
          <div className="customer-doc-add-panel__field-skeleton">
            <div className="customer-doc-add-panel__label-skeleton">
              <SkeletonBlock />
            </div>
            <div className="customer-doc-add-panel__input-skeleton">
              <SkeletonBlock />
            </div>
          </div>
          {/* mirrors field-group: label + RTE */}
          <div className="customer-doc-add-panel__field-skeleton customer-doc-add-panel__field-skeleton--grow">
            <div className="customer-doc-add-panel__label-skeleton">
              <SkeletonBlock />
            </div>
            <div className="customer-doc-add-panel__content-skeleton">
              <SkeletonBlock />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`attr-descriptions-panel customer-doc-add-panel${mode === 'paste' ? ' customer-doc-add-panel--paste' : ''}`}
    >
      <form id={formId} onSubmit={handleSubmit} className="customer-doc-add-panel__form">
        <TextField
          label="Title (optional)"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={mode === 'paste' ? 'e.g. "Q1 discovery call notes"' : 'e.g. "Product brief"'}
          autoComplete="off"
        />

        {mode === 'paste' ? (
          <div className="customer-doc-add-panel__field-group">
            <label className="customer-doc-add-panel__label">
              Content <span aria-hidden="true">*</span>
            </label>
            <RichTextEditor
              content={pasteBody}
              onUpdate={(json, text) => {
                setPasteBody(json);
                setPasteText(text);
              }}
              placeholder="Type / to view controls"
              toolbar
            />
          </div>
        ) : (
          <div className="customer-doc-add-panel__dropzone">
            <label
              className={`customer-doc-add-panel__file-label${isDragging ? ' customer-doc-add-panel__file-label--dragging' : ''}`}
              onDragOver={(e) => {
                e.preventDefault();
                setIsDragging(true);
              }}
              onDragEnter={(e) => {
                e.preventDefault();
                setIsDragging(true);
              }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={(e) => {
                e.preventDefault();
                setIsDragging(false);
                const file = e.dataTransfer.files[0];
                if (file) setSelectedFile(file);
              }}
            >
              <input
                type="file"
                accept=".txt"
                className="customer-doc-add-panel__file-input"
                onChange={(e) => setSelectedFile(e.target.files?.[0] ?? null)}
                aria-label="Select file"
              />
              {selectedFile ? (
                <>
                  <span className="customer-doc-add-panel__file-name">{selectedFile.name}</span>
                  <span className="customer-doc-add-panel__file-size">
                    {formatBytes(selectedFile.size)}
                  </span>
                </>
              ) : (
                <>
                  <span className="customer-doc-add-panel__upload-icon" aria-hidden="true">
                    ↑
                  </span>
                  <span>Drop file here or click to select</span>
                  <span className="customer-doc-add-panel__file-hint">.txt only (5 MB max)</span>
                </>
              )}
            </label>
          </div>
        )}

        {!hideActions && (
          <div className="customer-doc-add-panel__actions">
            <Button type="button" variant="ghost" size="sm" onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              size="sm"
              disabled={!isValid || saveState !== 'idle'}
            >
              {saveState === 'saving' ? 'Saving…' : saveState === 'saved' ? 'Saved' : 'Save'}
            </Button>
          </div>
        )}
      </form>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CustomerDocumentViewPanel
// ─────────────────────────────────────────────────────────────────────────────

interface CustomerDocumentViewPanelProps {
  customerUuid: string;
  docUuid: string;
  sourceKind: DocumentSourceKind;
  onChunkStatusChange?: (chunkStatus: DocumentChunkStatus | null) => void;
  onExtractRequest?: () => void;
  /**
   * undefined  = extraction not active (show doc body)
   * null       = extraction polling (show spinning button + skeleton body)
   * array      = extraction complete (hide button, show review body)
   */
  extractionResults?: ExtractionResult[] | null;
  existingValues?: Record<string, string | null>;
  onApplyExtraction?: (accepted: ExtractionResult[]) => void;
  onCancelExtraction?: () => void;
  /** Fires on every selection toggle with the currently accepted list - page header uses this for Apply count. */
  onExtractionSelectionChange?: (accepted: ExtractionResult[]) => void;
  /** Attribute definitions used to resolve field labels and section grouping in the extraction review. */
  attributeDefs?: TemplateAttribute[];
  /** When true, the Apply and Cancel buttons are disabled while the apply request is in flight. */
  applyInProgress?: boolean;
  /** Template sections for resolving section labels from the database. */
  sections?: CustomerTemplateSection[];
}

export function CustomerDocumentViewPanel({
  customerUuid,
  docUuid,
  sourceKind,
  onChunkStatusChange,
  onExtractRequest,
  extractionResults,
  existingValues,
  onApplyExtraction,
  onCancelExtraction,
  onExtractionSelectionChange,
  attributeDefs,
  applyInProgress,
  sections,
}: CustomerDocumentViewPanelProps) {
  const [doc, setDoc] = React.useState<{
    title: string;
    updatedAt: string;
    chunkStatus: DocumentChunkStatus | null;
    content: Record<string, unknown>;
  } | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);

  React.useEffect(() => {
    let cancelled = false;
    let pollTimer: ReturnType<typeof setTimeout> | null = null;

    const loadDocument = async (showLoading = false) => {
      if (showLoading) setIsLoading(true);
      try {
        const { document: d } = await customersApi.getDocument(customerUuid, docUuid);
        if (cancelled) return;
        setDoc({
          title: d.title,
          updatedAt: d.updatedAt,
          chunkStatus: d.chunkStatus,
          content: parseContentToTiptap(d.contentText),
        });
        onChunkStatusChange?.(d.chunkStatus);
        if (d.chunkStatus === 'pending') {
          pollTimer = setTimeout(() => {
            void loadDocument(false);
          }, 1500);
        }
      } catch (error) {
        if (!cancelled) console.error('[CustomerRightRailPanels] failed to load document', error);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    void loadDocument(true);

    return () => {
      cancelled = true;
      if (pollTimer) clearTimeout(pollTimer);
    };
  }, [customerUuid, docUuid, onChunkStatusChange]);

  if (isLoading) {
    return (
      <div className="customer-doc-view-panel">
        <div className="customer-doc-view-panel__header">
          <div className="customer-doc-view-panel__title-skeleton">
            <SkeletonBlock />
          </div>
          <div className="customer-doc-view-panel__meta-skeleton">
            <SkeletonBlock />
          </div>
        </div>
        <div className="customer-doc-view-panel__body">
          <div className="customer-doc-view-panel__body-skeleton">
            <SkeletonBlock />
          </div>
        </div>
      </div>
    );
  }

  if (!doc) return null;

  const isPolling = extractionResults !== undefined && extractionResults === null;
  const isDone = extractionResults !== undefined && extractionResults !== null;

  return (
    <div className="customer-doc-view-panel">
      <div className="customer-doc-view-panel__header">
        <div className="customer-doc-view-panel__title-row">
          <h3 className="customer-doc-view-panel__title">
            <span className="customer-doc-view-panel__title-text">{doc.title}</span>
            <span className="customer-docs-panel__kind-icon" aria-hidden="true">
              {sourceKind === 'upload' ? (
                <FileIcon width={12} height={12} />
              ) : (
                <LetterCaseUppercaseIcon width={12} height={12} />
              )}
            </span>
          </h3>
          {onExtractRequest &&
            !isDone &&
            (() => {
              const isProcessing = doc.chunkStatus === 'pending';
              const canGenerate = doc.chunkStatus === 'chunked' && !isPolling;
              return (
                <Tooltip.Root>
                  <Tooltip.Trigger asChild>
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        cursor: isPolling || isProcessing ? 'not-allowed' : undefined,
                      }}
                    >
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        disabled={!canGenerate}
                        style={!canGenerate ? { pointerEvents: 'none' } : undefined}
                        onClick={onExtractRequest}
                      >
                        {isPolling ? (
                          <>
                            <ArchonIcon size={IconSize.sm} isRunning />
                            &nbsp;Running
                          </>
                        ) : (
                          <>
                            <ArchonIcon size={IconSize.sm} />
                            &nbsp;Archon
                          </>
                        )}
                      </Button>
                    </span>
                  </Tooltip.Trigger>
                  {isProcessing && !isPolling && (
                    <Tooltip.Portal>
                      <Tooltip.Content
                        side="bottom"
                        align="center"
                        sideOffset={4}
                        className="ui-tooltip"
                      >
                        Document is being processed
                        <Tooltip.Arrow className="ui-tooltip-arrow" />
                      </Tooltip.Content>
                    </Tooltip.Portal>
                  )}
                </Tooltip.Root>
              );
            })()}
        </div>
        <span className="customer-doc-view-panel__meta">
          {formatRelativeTime(doc.updatedAt)}
          {isDocumentProcessing(doc.chunkStatus) && <DocumentProcessingStatus />}
        </span>
      </div>
      <div className="customer-doc-view-panel__body">
        {isPolling ? (
          <div className="customer-doc-view-panel__extraction-skeleton">
            <SkeletonBlock className="attr-descriptions-panel__skeleton" />
            <SkeletonBlock className="attr-descriptions-panel__skeleton" />
            <SkeletonBlock className="attr-descriptions-panel__skeleton" />
            <SkeletonBlock className="attr-descriptions-panel__skeleton" />
          </div>
        ) : isDone ? (
          <CustomerExtractionReviewPanel
            results={extractionResults}
            existingValues={existingValues ?? {}}
            onApply={onApplyExtraction ?? (() => {})}
            onCancel={onCancelExtraction ?? (() => {})}
            onSelectionChange={onExtractionSelectionChange}
            attributeDefs={attributeDefs}
            applyInProgress={applyInProgress}
            sections={sections}
            hideFooter
          />
        ) : (
          <RichTextEditor content={doc.content} readOnly />
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CustomerExtractionReviewPanel
// ─────────────────────────────────────────────────────────────────────────────

interface CustomerExtractionReviewPanelProps {
  results: ExtractionResult[] | null; // null while polling - shows loading skeleton
  /** Existing field values keyed by fieldKey. */
  existingValues: Record<string, string | null>;
  onApply: (accepted: ExtractionResult[]) => void;
  onCancel: () => void;
  /** Emits currently-selected results on every toggle - used to drive page-header Apply button. */
  onSelectionChange?: (accepted: ExtractionResult[]) => void;
  /** When true, the Cancel / Apply footer inside the panel is suppressed (page header owns those actions). */
  hideFooter?: boolean;
  /** Attribute definitions used to resolve field labels and section grouping. */
  attributeDefs?: TemplateAttribute[];
  /** When true, checkboxes are disabled while the apply request is in flight. */
  applyInProgress?: boolean;
  /** Template sections for resolving section labels from the database. */
  sections?: CustomerTemplateSection[];
}

export function CustomerExtractionReviewPanel({
  results,
  existingValues,
  onApply,
  onCancel,
  onSelectionChange,
  hideFooter,
  attributeDefs,
  applyInProgress,
  sections,
}: CustomerExtractionReviewPanelProps) {
  const [selected, setSelected] = React.useState<Set<string>>(new Set());

  // Initialise selection when results arrive: auto-check high+medium, leave low unchecked
  React.useEffect(() => {
    if (!results) return;
    const autoSelected = new Set(
      results
        .filter((r) => r.value !== null && (r.confidence === 'high' || r.confidence === 'medium'))
        .map((r) => r.fieldKey)
    );
    setSelected(autoSelected);
  }, [results]);

  React.useEffect(() => {
    if (!results) return;
    onSelectionChange?.(results.filter((r) => selected.has(r.fieldKey)));
  }, [selected, results, onSelectionChange]);

  if (!results) {
    return (
      <div className="attr-descriptions-panel extraction-review-panel">
        <SkeletonBlock className="attr-descriptions-panel__skeleton" />
        <SkeletonBlock className="attr-descriptions-panel__skeleton" />
        <SkeletonBlock className="attr-descriptions-panel__skeleton" />
        <SkeletonBlock className="attr-descriptions-panel__skeleton" />
      </div>
    );
  }

  // Build a lookup map from fieldKey → TemplateAttribute for labels and section assignment
  const attrMap = new Map((attributeDefs ?? []).map((a) => [a.fieldKey, a]));

  // Determine which section a field belongs to: prefer attributeDefs lookup, fallback to prefix match
  const sectionKeys = (sections ?? []).map((s) => s.sectionKey);
  const getSectionKey = (fieldKey: string): string => {
    const attr = attrMap.get(fieldKey);
    if (attr) return attr.sectionKey;
    const match = sectionKeys.find((key) => fieldKey.startsWith(key + '_') || fieldKey === key);
    return match ?? 'other';
  };

  // Group results by sectionKey following section order (DB-driven when available)
  const orderedSections = sections
    ? [...sections]
        .sort((a, b) => a.displayOrder - b.displayOrder)
        .map((s) => ({ value: s.sectionKey, label: s.label }))
    : [];
  const grouped = orderedSections
    .map(({ value: sectionKey, label: sectionLabel }) => ({
      sectionKey,
      sectionLabel,
      items: results.filter((r) => getSectionKey(r.fieldKey) === sectionKey),
    }))
    .filter((g) => g.items.length > 0);

  // Collect remaining results not matched to a known section
  const groupedKeys = new Set(grouped.flatMap((g) => g.items.map((r) => r.fieldKey)));
  const ungrouped = results.filter((r) => !groupedKeys.has(r.fieldKey));
  if (ungrouped.length > 0) {
    grouped.push({ sectionKey: 'other', sectionLabel: 'Other', items: ungrouped });
  }

  const handleToggle = (fieldKey: string, hasValue: boolean) => {
    if (!hasValue) return; // not-found rows are disabled
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(fieldKey)) {
        next.delete(fieldKey);
      } else {
        next.add(fieldKey);
      }
      return next;
    });
  };

  const handleApply = () => {
    const accepted = results.filter((r) => selected.has(r.fieldKey));
    onApply(accepted);
  };

  return (
    <div className="attr-descriptions-panel extraction-review-panel">
      {grouped.map(({ sectionKey, sectionLabel, items }) => (
        <section key={sectionKey} className="extraction-review-panel__section">
          <h4 className="extraction-review-panel__section-title">{sectionLabel}</h4>
          <ul className="extraction-review-panel__list" role="list">
            {items.map((result) => {
              const hasValue = result.value !== null;
              const isChecked = selected.has(result.fieldKey);
              const current = existingValues[result.fieldKey] ?? null;
              return (
                <li
                  key={result.fieldKey}
                  className={`extraction-review-panel__row${!hasValue ? ' extraction-review-panel__row--not-found' : ''}`}
                >
                  <label className="extraction-review-panel__row-label">
                    <input
                      type="checkbox"
                      className="extraction-review-panel__checkbox"
                      checked={isChecked}
                      disabled={!hasValue || !!applyInProgress}
                      onChange={() => handleToggle(result.fieldKey, hasValue)}
                      aria-label={result.fieldKey}
                    />
                    <span className="extraction-review-panel__field-name">
                      {attrMap.get(result.fieldKey)?.name ?? result.fieldKey}
                    </span>
                    {!hasValue && (
                      <span className="extraction-review-panel__badge" aria-label="not found">
                        not found
                      </span>
                    )}
                  </label>
                  {hasValue && (
                    <div className="extraction-review-panel__comparison">
                      <div className="extraction-review-panel__comparison-row">
                        <span className="extraction-review-panel__comparison-label">Current</span>
                        <span className="extraction-review-panel__comparison-value extraction-review-panel__comparison-value--current">
                          {current ?? '-'}
                        </span>
                      </div>
                      <div className="extraction-review-panel__comparison-row">
                        <span className="extraction-review-panel__comparison-label">New</span>
                        <span className="extraction-review-panel__comparison-value extraction-review-panel__comparison-value--new">
                          {result.value}
                        </span>
                      </div>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </section>
      ))}

      {!hideFooter && (
        <div className="extraction-review-panel__footer">
          <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            size="sm"
            disabled={selected.size === 0}
            onClick={handleApply}
          >
            Apply ({selected.size})
          </Button>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CustomerImportCsvPanel
// ─────────────────────────────────────────────────────────────────────────────

export interface CsvPreviewCustomer {
  name: string;
  email: string | null;
  phone: string | null;
  linkedin: string | null;
  /** new = would be imported; duplicate = email exists; no_contact = no email/phone/linkedin on row */
  status: 'new' | 'duplicate' | 'no_contact';
  /** For duplicates: which identifier caused the match */
  matchedBy: 'email' | 'linkedin' | null;
  extra: Array<{ fieldKey: string; label: string; value: string }>;
}

export interface CsvPreviewRow {
  name: string;
  email?: string;
}

export interface CsvPreviewMapping {
  csvColumn: string;
  fieldKey: string;
  fieldLabel: string;
}

export interface CustomerImportCsvPanelProps {
  /** null = polling; object = preview ready */
  preview: {
    filename: string;
    totalRows: number;
    newRows: number;
    duplicateRows: number;
    noContactRows: number;
    mappedColumns: number;
    mappings: CsvPreviewMapping[];
    preview: CsvPreviewCustomer[];
  } | null;
}

export function CustomerImportCsvPanel({ preview }: CustomerImportCsvPanelProps) {
  const [showMappings, setShowMappings] = React.useState(false);

  React.useEffect(() => {
    setShowMappings(false);
  }, [preview?.filename]);

  if (!preview) {
    return (
      <div className="customer-import-csv-panel customer-import-csv-panel--polling">
        {/* Meta section skeleton */}
        <div className="customer-import-csv-panel__meta">
          <div style={{ width: '100%', height: '14px', borderRadius: 'var(--radius-sm)' }}>
            <SkeletonBlock />
          </div>
          <div style={{ width: '100%', height: '12px', borderRadius: 'var(--radius-sm)' }}>
            <SkeletonBlock />
          </div>
        </div>
        {/* Row skeletons */}
        <ul className="customer-import-csv-panel__list" role="list">
          {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
            <li key={i} className="customer-import-csv-panel__row">
              <div
                style={{
                  width: '100%',
                  height: '14px',
                  borderRadius: 'var(--radius-sm)',
                }}
              >
                <SkeletonBlock />
              </div>
              <div
                style={{
                  width: '100%',
                  height: '12px',
                  borderRadius: 'var(--radius-sm)',
                }}
              >
                <SkeletonBlock />
              </div>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  const filename = preview.filename;
  const newRows = preview.newRows;
  const duplicateRows = preview.duplicateRows;
  const noContactRows = preview.noContactRows;
  const mappings = Array.isArray(preview.mappings) ? preview.mappings : [];
  const rows = Array.isArray(preview.preview) ? preview.preview : [];
  const newRowsList = rows.filter((r) => r.status === 'new');
  const duplicateRowsList = rows.filter((r) => r.status === 'duplicate');
  const noContactRowsList = rows.filter((r) => r.status === 'no_contact');

  return (
    <div className="customer-import-csv-panel">
      <div className="customer-import-csv-panel__meta">
        <div className="customer-import-csv-panel__meta-top">
          <span className="customer-import-csv-panel__filename">{filename}</span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-auto p-1 text-xs text-muted-foreground hover:bg-transparent hover:text-foreground"
            onClick={() => setShowMappings((value) => !value)}
          >
            {showMappings ? 'Hide mapping' : 'Show mapping'}
          </Button>
        </div>
        <span className="customer-import-csv-panel__count">
          {newRows} new
          {duplicateRows > 0 ? `, ${duplicateRows} duplicated` : ''}
          {noContactRows > 0 ? `, ${noContactRows} no contact info` : ''}
          {duplicateRows > 0 ? ' - by email, LinkedIn' : ''}
        </span>
        {showMappings && (
          <div className="customer-import-csv-panel__mapping" role="list">
            {mappings.length > 0 ? (
              mappings.map((mapping) => (
                <div
                  key={`${mapping.csvColumn}-${mapping.fieldKey}`}
                  className="customer-import-csv-panel__mapping-row"
                  role="listitem"
                >
                  <span className="customer-import-csv-panel__mapping-column">
                    {mapping.csvColumn}
                  </span>
                  <span className="customer-import-csv-panel__mapping-arrow">to</span>
                  <span className="customer-import-csv-panel__mapping-field">
                    {mapping.fieldLabel}
                  </span>
                </div>
              ))
            ) : (
              <span className="customer-import-csv-panel__mapping-empty">
                No inferred mappings were returned.
              </span>
            )}
          </div>
        )}
      </div>
      {newRowsList.length > 0 && (
        <>
          <div className="customer-import-csv-panel__section-header">New ({newRows})</div>
          <ul className="customer-import-csv-panel__list" role="list">
            {newRowsList.map((row, i) => (
              <li key={i} className="customer-import-csv-panel__row">
                <span className="customer-import-csv-panel__row-name">{row.name}</span>
                <span className="customer-import-csv-panel__row-identifiers">
                  {row.email ? (
                    <span className="customer-import-csv-panel__row-identifier">{row.email}</span>
                  ) : (
                    <span className="customer-import-csv-panel__row-identifier customer-import-csv-panel__row-identifier--missing">
                      no email
                    </span>
                  )}
                  {row.linkedin ? (
                    <span className="customer-import-csv-panel__row-identifier">
                      {row.linkedin}
                    </span>
                  ) : (
                    <span className="customer-import-csv-panel__row-identifier customer-import-csv-panel__row-identifier--missing">
                      no LinkedIn
                    </span>
                  )}
                </span>
              </li>
            ))}
          </ul>
        </>
      )}
      {noContactRowsList.length > 0 && (
        <>
          <div className="customer-import-csv-panel__section-header customer-import-csv-panel__section-header--muted">
            Contact info not found ({noContactRows})
          </div>
          <ul className="customer-import-csv-panel__list" role="list">
            {noContactRowsList.map((row, i) => (
              <li
                key={i}
                className="customer-import-csv-panel__row customer-import-csv-panel__row--duplicate"
              >
                <span className="customer-import-csv-panel__row-name">{row.name}</span>
                <span className="customer-import-csv-panel__row-email">no email · no LinkedIn</span>
              </li>
            ))}
          </ul>
        </>
      )}
      {duplicateRowsList.length > 0 && (
        <>
          <div className="customer-import-csv-panel__section-header customer-import-csv-panel__section-header--muted">
            Duplicated ({duplicateRows})
          </div>
          <ul className="customer-import-csv-panel__list" role="list">
            {duplicateRowsList.map((row, i) => {
              const matchedValue =
                row.matchedBy === 'email'
                  ? row.email
                  : row.matchedBy === 'linkedin'
                    ? row.linkedin
                    : null;
              const matchLabel =
                row.matchedBy === 'email'
                  ? 'email'
                  : row.matchedBy === 'linkedin'
                    ? 'LinkedIn'
                    : null;
              return (
                <li
                  key={i}
                  className="customer-import-csv-panel__row customer-import-csv-panel__row--duplicate"
                >
                  <div className="customer-import-csv-panel__row-top">
                    <span className="customer-import-csv-panel__row-name">{row.name}</span>
                    {matchLabel && (
                      <span className="customer-import-csv-panel__row-match-badge">
                        by {matchLabel}
                      </span>
                    )}
                  </div>
                  {matchedValue && (
                    <span className="customer-import-csv-panel__row-identifier">
                      {matchedValue}
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        </>
      )}
    </div>
  );
}

// CustomerImportArchonPanel
// ─────────────────────────────────────────────────────────────────────────────

export type ImportArchonPhase = 'input' | 'polling' | 'review';

export interface CustomerImportArchonPanelProps {
  /** null = input phase; state = polling or review */
  importPanel: {
    status: 'polling' | 'review';
    results?: ExtractionResult[];
  } | null;
  /** Attribute defs for field labels */
  attributeDefs: TemplateAttribute[];
  /** Emits text changes so the parent can enable/disable the header Import button */
  onTextChange?: (text: string) => void;
  /** Emits title changes so the parent can pass it when creating the document */
  onTitleChange?: (title: string) => void;
  /** Emits currently-selected results on every toggle */
  onSelectionChange?: (accepted: ExtractionResult[]) => void;
  /** When true, Create button is in-flight */
  createInProgress?: boolean;
  /** Set after a successful create - triggers the success state */
  createdCustomerUuid?: string | null;
  /** Called when the user wants to import another document */
  onImportAnother?: () => void;
  /** Set when the extracted email or linkedin matches an existing customer */
  duplicate?: {
    matchedBy: 'email' | 'linkedin';
    customerName: string | null;
    customerUuid: string | null;
  } | null;
  /** Template sections for resolving section labels from the database. */
  sections?: CustomerTemplateSection[];
}

const IMPORT_IDENTITY_SECTION = 'identity';

export function CustomerImportArchonPanel({
  importPanel,
  attributeDefs,
  onTextChange,
  onTitleChange,
  onSelectionChange,
  createInProgress,
  createdCustomerUuid,
  onImportAnother,
  duplicate,
  sections,
}: CustomerImportArchonPanelProps) {
  const [body, setBody] = React.useState<Record<string, unknown> | null>(null);
  const [title, setTitle] = React.useState('');
  const [selected, setSelected] = React.useState<Set<string>>(new Set());
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const results = importPanel?.results ?? null;

  // Auto-select high + medium confidence results when they arrive
  React.useEffect(() => {
    if (!results) return;
    const autoSelected = new Set(
      results
        .filter((r) => r.value !== null && (r.confidence === 'high' || r.confidence === 'medium'))
        .map((r) => r.fieldKey)
    );
    setSelected(autoSelected);
  }, [results]);

  React.useEffect(() => {
    if (!results) return;
    onSelectionChange?.(results.filter((r) => selected.has(r.fieldKey)));
  }, [selected, results, onSelectionChange]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    file
      .text()
      .then((raw) => {
        const parsed = parseContentToTiptap(raw);
        const plain = tiptapToPlainText(parsed);
        setBody(parsed);
        onTextChange?.(plain);
      })
      .catch(() => {});
    // Reset so the same file can be re-selected if needed
    e.target.value = '';
  };

  // ── Success phase ────────────────────────────────────────────────────────────
  if (createdCustomerUuid) {
    return (
      <div className="customer-import-archon-panel customer-import-archon-panel--success">
        <p className="customer-import-archon-panel__success-message">
          Customer created successfully.
        </p>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => {
            setBody(null);
            setTitle('');
            setSelected(new Set());
            onTextChange?.('');
            onTitleChange?.('');
            onImportAnother?.();
          }}
        >
          Import another document
        </Button>
      </div>
    );
  }

  // ── Input phase ─────────────────────────────────────────────────────────────
  if (!importPanel) {
    return (
      <div className="customer-import-archon-panel">
        <div className="customer-import-archon-panel__title-row">
          <TextField
            label="Title (optional)"
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
              onTitleChange?.(e.target.value);
            }}
            placeholder={`e.g. "John Smith \u2014 LinkedIn"`}
            autoComplete="off"
          />
          <input
            ref={fileInputRef}
            type="file"
            accept=".txt,.md,.text"
            className="customer-import-archon-panel__file-input"
            onChange={handleFileChange}
            aria-label="Upload text file"
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="customer-import-archon-panel__upload-btn"
            onClick={() => fileInputRef.current?.click()}
          >
            Upload file
          </Button>
        </div>
        <div className="customer-import-archon-panel__editor-wrap">
          <RichTextEditor
            content={body}
            onUpdate={(json, plainText) => {
              setBody(json);
              onTextChange?.(plainText);
            }}
            placeholder="Paste text here…"
            toolbar
          />
        </div>
      </div>
    );
  }

  // ── Polling phase ────────────────────────────────────────────────────────────
  if (importPanel.status === 'polling') {
    return (
      <div className="customer-import-archon-panel">
        <SkeletonBlock className="attr-descriptions-panel__skeleton" />
        <SkeletonBlock className="attr-descriptions-panel__skeleton" />
        <SkeletonBlock className="attr-descriptions-panel__skeleton" />
        <SkeletonBlock className="attr-descriptions-panel__skeleton" />
        <SkeletonBlock className="attr-descriptions-panel__skeleton" />
      </div>
    );
  }

  // ── Review phase ─────────────────────────────────────────────────────────────
  if (!results) return null;

  const attrMap = new Map((attributeDefs ?? []).map((a) => [a.fieldKey, a]));

  const IDENTITY_KEYS = new Set(['name', 'email', 'phone']);
  const importSectionKeys = (sections ?? []).map((s) => s.sectionKey);

  const getSectionKey = (fieldKey: string): string => {
    if (IDENTITY_KEYS.has(fieldKey)) return IMPORT_IDENTITY_SECTION;
    const attr = attrMap.get(fieldKey);
    if (attr) return attr.sectionKey;
    const match = importSectionKeys.find(
      (key) => fieldKey.startsWith(key + '_') || fieldKey === key
    );
    return match ?? 'other';
  };

  // Always show identity section first, then the remaining sections
  const orderedSections = sections
    ? [...sections]
        .sort((a, b) => a.displayOrder - b.displayOrder)
        .map((s) => ({ value: s.sectionKey, label: s.label }))
    : [];
  const allSections = [
    { value: IMPORT_IDENTITY_SECTION, label: resolveGroupLabel(IMPORT_IDENTITY_SECTION, sections) },
    ...orderedSections.filter((g) => g.value !== IMPORT_IDENTITY_SECTION),
  ];

  const grouped = allSections
    .map(({ value: sectionKey, label: sectionLabel }) => ({
      sectionKey,
      sectionLabel,
      items: results.filter((r) => getSectionKey(r.fieldKey) === sectionKey),
    }))
    .filter((g) => g.items.length > 0);

  const groupedKeys = new Set(grouped.flatMap((g) => g.items.map((r) => r.fieldKey)));
  const ungrouped = results.filter((r) => !groupedKeys.has(r.fieldKey));
  if (ungrouped.length > 0) {
    grouped.push({ sectionKey: 'other', sectionLabel: 'Other', items: ungrouped });
  }

  const handleToggle = (fieldKey: string, hasValue: boolean) => {
    if (!hasValue) return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(fieldKey)) {
        next.delete(fieldKey);
      } else {
        next.add(fieldKey);
      }
      return next;
    });
  };

  const getFieldLabel = (fieldKey: string): string => {
    if (fieldKey === 'name') return 'Name';
    if (fieldKey === 'email') return 'Email';
    if (fieldKey === 'phone') return 'Phone';
    return attrMap.get(fieldKey)?.name ?? fieldKey;
  };

  return (
    <div className="attr-descriptions-panel extraction-review-panel customer-import-archon-panel--review">
      {duplicate && (
        <div className="customer-import-archon-panel__duplicate-warning" role="alert">
          <strong>Duplicate detected.</strong>{' '}
          {duplicate.customerName
            ? `A customer named "${duplicate.customerName}" already has this ${duplicate.matchedBy === 'email' ? 'email address' : 'LinkedIn profile'}.`
            : `A customer already has this ${duplicate.matchedBy === 'email' ? 'email address' : 'LinkedIn profile'}.`}{' '}
          Creating a duplicate is disabled.
        </div>
      )}
      {grouped.map(({ sectionKey, sectionLabel, items }) => (
        <section key={sectionKey} className="extraction-review-panel__section">
          <h4 className="extraction-review-panel__section-title">{sectionLabel}</h4>
          <ul className="extraction-review-panel__list" role="list">
            {items.map((result) => {
              const hasValue = result.value !== null;
              const isChecked = selected.has(result.fieldKey);
              return (
                <li
                  key={result.fieldKey}
                  className={`extraction-review-panel__row${!hasValue ? ' extraction-review-panel__row--not-found' : ''}`}
                >
                  <label className="extraction-review-panel__row-label">
                    <input
                      type="checkbox"
                      className="extraction-review-panel__checkbox"
                      checked={isChecked}
                      disabled={!hasValue || !!createInProgress}
                      onChange={() => handleToggle(result.fieldKey, hasValue)}
                      aria-label={getFieldLabel(result.fieldKey)}
                    />
                    <span className="extraction-review-panel__field-name">
                      {getFieldLabel(result.fieldKey)}
                    </span>
                    {!hasValue && (
                      <span className="extraction-review-panel__badge" aria-label="not found">
                        not found
                      </span>
                    )}
                  </label>
                  {hasValue && (
                    <div className="extraction-review-panel__comparison">
                      <div className="extraction-review-panel__comparison-row">
                        <span className="extraction-review-panel__comparison-label">New</span>
                        <span className="extraction-review-panel__comparison-value extraction-review-panel__comparison-value--new">
                          {result.value}
                        </span>
                      </div>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </div>
  );
}
